import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import MagicLinkCallback from "@/pages/MagicLinkCallback";
import ModuleSetup from "@/pages/ModuleSetup";
import Library from "@/pages/Library";
import Home from "@/pages/Home";
import Treasury from "@/pages/Treasury";
import Incomes from "@/pages/Incomes";
import Clients from "@/pages/Clients";
import Quotes from "@/pages/Quotes";
import Invoices from "@/pages/Invoices";
import SiteOrders from "@/pages/SiteOrders";
import MaterialLists from "@/pages/MaterialLists";
import CompanyProfile from "@/pages/CompanyProfile";
import Stock from "@/pages/Stock";
import Subscription from "@/pages/Subscription";
import Packs from "@/pages/Packs";
import PacksVitrine from "@/pages/PacksVitrine";
import Team from "@/pages/Team";
import PlatformAdmin from "@/pages/PlatformAdmin";
import AllowlistAdmin from "@/pages/AllowlistAdmin";
import Landing from "@/pages/Landing";
import BrandAssets from "@/pages/BrandAssets";
import CGU from "@/pages/legal/CGU";
import Confidentialite from "@/pages/legal/Confidentialite";
import MentionsLegales from "@/pages/legal/MentionsLegales";

// Smart root : authentifié → dashboard ; sinon → landing marketing
function SmartRoot() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="flex items-center gap-3 text-secondary-text">
          <div className="spinner" /><span>Chargement…</span>
        </div>
      </div>
    );
  }
  if (user) return <AppLayout><Home /></AppLayout>;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection: when returning from Google OAuth the URL has
  // #session_id=... — route to AuthCallback BEFORE ProtectedRoute runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/magic" element={<MagicLinkCallback />} />
      <Route path="/packs-vitrine" element={<PacksVitrine />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/cgv" element={<CGU />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/brand" element={<BrandAssets />} />
      <Route
        path="/"
        element={<SmartRoot />}
      />
      <Route
        path="/abonnement"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Subscription />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipe"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Team />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packs"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Packs />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/plateforme"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PlatformAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/allowlist"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AllowlistAdmin />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ModuleSetup />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Library />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/treasury"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Treasury />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/encaissements"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Incomes />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Clients />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Quotes />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Invoices />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-orders"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SiteOrders />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/material-lists"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MaterialLists />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CompanyProfile />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Stock />
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
